import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
// Single source of truth for the sort enum lives in the API layer so the
// server signature and the hook can't drift.
import type { AgencyTopPoliciesSortBy } from "@/api/agencies";
import { apiGet, asOverrideKey } from "@/lib/api-client";
import { agencyKeys } from "@/lib/query";
import { AgencyDetailSchema } from "@/schemas/agent";

export type { AgencyTopPoliciesSortBy } from "@/api/agencies";

const PolicySummarySchema = z.object({
  totalPolicies: z.number(),
  activePolicies: z.number(),
});

const ClaimSummarySchema = z.object({
  totalClaims: z.number(),
  openClaims: z.number(),
  totalPaid: z.number(),
  totalReserved: z.number(),
  totalRecovered: z.number(),
  totalIncurred: z.number(),
});

const AgencyContactSchema = z.object({
  id: z.string(),
  contactName: z.string(),
  contactTitle: z.string().nullable(),
  contactEmail: z.string().nullable(),
  contactPhone: z.string().nullable(),
  contactFax: z.string().nullable(),
  contactType: z.string().nullable(),
  isActive: z.boolean(),
});
const AgencyContactsSchema = z.array(AgencyContactSchema);
export type AgencyContact = z.infer<typeof AgencyContactSchema>;

const AgencyTopPolicySchema = z.object({
  policyNumber: z.string(),
  insuredName: z.string().nullable(),
  policyStatus: z.string().nullable(),
  effectiveDate: z.string().nullable(),
  expirationDate: z.string().nullable(),
  writtenPremium: z.number().nullable(),
  incurredLoss: z.number().nullable(),
});
const AgencyTopPoliciesSchema = z.array(AgencyTopPolicySchema);

// `?as=<sam>` impersonation flips the effective identity for restriction
// checks at the API layer. Pin every agency cache to the override so
// switching identities mid-session does not serve the prior identity's
// restricted-claim-aware rollups.
export function useAgency(agentNumber: string) {
  return useQuery({
    queryKey: [...agencyKeys.detail(agentNumber), asOverrideKey()] as const,
    queryFn: () => apiGet(`/api/agencies/${encodeURIComponent(agentNumber)}`, AgencyDetailSchema),
  });
}

export function useAgencyPolicySummary(agentNumber: string) {
  return useQuery({
    queryKey: [...agencyKeys.policies(agentNumber), asOverrideKey()] as const,
    queryFn: () =>
      apiGet(`/api/agencies/${encodeURIComponent(agentNumber)}/policies`, PolicySummarySchema),
  });
}

export function useAgencyClaimSummary(agentNumber: string) {
  return useQuery({
    queryKey: [...agencyKeys.claims(agentNumber), asOverrideKey()] as const,
    queryFn: () =>
      apiGet(`/api/agencies/${encodeURIComponent(agentNumber)}/claims`, ClaimSummarySchema),
  });
}

export type AgencyTopPolicy = z.infer<typeof AgencyTopPolicySchema>;

export function useAgencyTopPolicies(
  agentNumber: string,
  sortBy: AgencyTopPoliciesSortBy,
  enabled = true,
) {
  return useQuery({
    queryKey: [...agencyKeys.policies(agentNumber), "top", sortBy, asOverrideKey()] as const,
    queryFn: () =>
      apiGet(
        `/api/agencies/${encodeURIComponent(agentNumber)}/top-policies?sortBy=${sortBy}&limit=25`,
        AgencyTopPoliciesSchema,
      ),
    enabled,
  });
}

export function useAgencyContacts(agentNumber: string, enabled = true) {
  return useQuery({
    queryKey: [...agencyKeys.contacts(agentNumber), asOverrideKey()] as const,
    queryFn: () =>
      apiGet(`/api/agencies/${encodeURIComponent(agentNumber)}/contacts`, AgencyContactsSchema),
    enabled: enabled && agentNumber.length > 0,
  });
}
